import * as Raven from 'raven-js';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import { ModalModule } from 'ngx-bootstrap/modal';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { AlertModule } from 'ngx-bootstrap/alert';

import { environment } from '../environments/environment';

import { AppComponent } from './app.component';

import { DashboardPage } from './components/pages/dashboard/dashboard.component';

import { AuthRESTService } from './components/services/rest/auth-rest.service';
import { GetLoginUrlService } from './services/get-login-url';
import { UserLangResolverService } from './services/user-lang-resolver';
import { GetConfigService } from './components/services/get-config.service';

import { LoginPage } from './components/pages/login/login.component';

import { PopoverModule } from 'ngx-bootstrap/popover';
import { AuthControllerService } from './services/auth-controller/auth-controller.service';
import { AuthGuard } from './guards/auth.guard';
import { ToastrModule } from 'ngx-toastr';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { TokenInterceptor } from './interceptors/token.interceptor';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { TranslationComponent } from './components/pages/translations/translation.component';
// import { AppRoutingModule } from './app-routing.module';
import { RouterModule } from '@angular/router';
import { UserPage } from './components/pages/user/user-edit/user-edit.component';
import { RegistrationFormFieldsList } from './components/pages/configure-registration-form/form-fields-list/registration-form-fields-list.component';
import { UsersPage } from './components/pages/user/user-list/user-list.component';
import { SettingsComponent } from './components/pages/settings/settings.component';
import { PartnerCreateComponent } from './components/pages/partners/partner-create/partner-create.component';
import { PartnerEditComponent } from './components/pages/partners/partner-edit/partner-edit.component';
import { PartnerListComponent } from './components/pages/partners/partner-list/partner-list.component';
import { OaclientCreateComponent } from './components/pages/oaclients/oaclient-create/oaclient-create.component';
import { OaclientEditComponent } from './components/pages/oaclients/oaclient-edit/oaclient-edit.component';
import { OaclientListComponent } from './components/pages/oaclients/oaclient-list/oaclient-list.component';
import { UserCreatePage } from './components/pages/user/user-create/user-create.component';
import { FormFieldEditComponent } from './components/pages/configure-registration-form/form-field-edit/form-field-edit.component';

export class RavenErrorHandler implements ErrorHandler {
  handleError(err: any): void {
    Raven.captureException(err.originalError || err);
    throw err;
  }
}

let providers: any[] = [
  AuthControllerService,
  AuthRESTService,
  AuthGuard,
  GetLoginUrlService,
  UserLangResolverService,
  GetConfigService,
  {
    provide: HTTP_INTERCEPTORS,
    useClass: TokenInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ErrorInterceptor,
    multi: true,
  },
];

const isLocalhost =
  location.hostname === 'localhost' ||
  location.hostname === '127.0.0.1' ||
  location.hostname === '';
if (!isLocalhost) {
  Raven.config(
    'https://2c7ee078962d43a1a10ad9158fc1b86f@sentry.io/218403'
  ).install();

  (<any>window).onunhandledrejection = function (evt) {
    Raven.captureException(evt);
  };

  (<any>window).onerror = function (evt) {
    Raven.captureException(evt);
  };

  providers.push({ provide: ErrorHandler, useClass: RavenErrorHandler });
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    environment.casBaseUrl + 'translations/',
    ''
  );
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ToastrModule.forRoot(),
    PopoverModule.forRoot(),
    RouterModule.forRoot([
      {
        path: '',
        component: DashboardPage,
        canActivate: [AuthGuard],
      },
      {
        path: 'login',
        component: LoginPage,
      },
      {
        path: 'dashboard',
        component: DashboardPage,
        canActivate: [AuthGuard],
      },
      {
        path: 'configure-registration-form',
        component: RegistrationFormFieldsList,
        canActivate: [AuthGuard],
      },
      {
        path: 'configure-registration-form/:id/edit',
        component: FormFieldEditComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'user-personal',
        component: UserPage,
        canActivate: [AuthGuard],
      },
      {
        path: 'users',
        component: UsersPage,
        canActivate: [AuthGuard],
      },
      {
        path: 'user/:id/edit',
        component: UserPage,
        canActivate: [AuthGuard],
      },
      {
        path: 'user/create',
        component: UserCreatePage,
        canActivate: [AuthGuard],
      },
      {
        path: 'oaclients',
        component: OaclientListComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'oaclient/:id/edit',
        component: OaclientEditComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'oaclient/create',
        component: OaclientCreateComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'translations',
        component: TranslationComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'partners',
        component: PartnerListComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'partner/:id/edit',
        component: PartnerEditComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'partner/create',
        component: PartnerCreateComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'settings',
        component: SettingsComponent,
        canActivate: [AuthGuard],
      },
    ]),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    BsDropdownModule.forRoot(),
    ModalModule.forRoot(),
    CollapseModule.forRoot(),
    TabsModule.forRoot(),
    AlertModule.forRoot(),
  ],
  providers,
  bootstrap: [AppComponent],
})
export class AppModule {}
